import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { useInternalMessagesEventGroup } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/useInternalMessages";
import { EventGroup } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { TrainListSection } from "features/VehicleSidebar/VehicleList/TrainList/TrainListSection";
import {
  TrainData,
  trainListSchema,
} from "features/VehicleSidebar/VehicleList/TrainList/types";
import {
  filterTrainList,
  sortTrainData,
} from "features/VehicleSidebar/VehicleList/TrainList/utils";
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "shared/components/Spor";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import { useInfiniteScroll } from "shared/hooks/useInfiniteScroll";
import { getDate } from "shared/utils/dateTimeUtils";
import * as z from "zod";

type TrainListProps = {
  searchQuery: string;
  setSearchHits: Dispatch<SetStateAction<number>>;
  selectedDate: Date;
  scrollBoxRef: RefObject<HTMLDivElement | null>;
  showTrainsMissingData: boolean;
};

export const groupBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) =>
  arr.reduce(
    (groups, item) => {
      // eslint-disable-next-line no-param-reassign -- modifying the object is fine as long as the computed result is returned from the reduce function
      (groups[key(item)] ||= []).push(item);
      return groups;
    },
    {} as Record<K, T[]>,
  );

export const indexBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) =>
  arr.reduce(
    (acc, item) => {
      acc[key(item)] = item;
      return acc;
    },
    {} as Record<K, T>,
  );

const TrainList = ({
  searchQuery,
  setSearchHits,
  selectedDate,
  scrollBoxRef,
  showTrainsMissingData,
}: TrainListProps) => {
  const day = getDate(selectedDate);
  const countryCodeForDropsRole = useCountryCodeForDropsRole();

  const { status, data } = useQuery({
    queryKey: [
      "trainList",
      day,
      countryCodeForDropsRole,
      showTrainsMissingData,
    ],
    refetchInterval: 60000,
    queryFn: async ({ signal }) =>
      axiosClient
        .get<z.infer<typeof trainListSchema>>(
          `${getBackendUrl()}/trainList/${countryCodeForDropsRole}?date=${day}&unknownCompany=${showTrainsMissingData}`,
          {
            signal,
          },
        )
        .then((res) => trainListSchema.parse(res.data)),
  });

  const { data: internalMessages } = useInternalMessagesEventGroup(
    EventGroup.TRAIN,
  );

  const trainList = status === "success" ? data.trainList : [];
  const filteredAndSortedTrains = useMemo(
    () =>
      trainList
        .filter((train) => filterTrainList(train, searchQuery, selectedDate))
        .sort((a, b) => sortTrainData(a, b, searchQuery)),
    [searchQuery, selectedDate, trainList],
  );

  useEffect(
    () => setSearchHits(filteredAndSortedTrains.length),
    [filteredAndSortedTrains],
  );

  const { numberOfItemsToRender: numberOfItemsToRenderNoSearch } =
    useInfiniteScroll({
      containerRef: scrollBoxRef,
      initialItemsToRender: 20,
      maxItemsToRender: filteredAndSortedTrains.length,
    });

  // Disable infinite scroll for searches since it includes yesterday/today/tomorrow and thus behaves weirdly
  const numberOfItemsToRender =
    searchQuery.length >= 2 ? undefined : numberOfItemsToRenderNoSearch;

  const groupedTrainData: TrainData[][] = Object.values(
    groupBy(
      filteredAndSortedTrains.slice(0, numberOfItemsToRender),
      (train: TrainData) => train.trainDate,
    ),
  );

  const { t } = useTranslation();

  switch (status) {
    case "pending":
      return <SkeletonLoader skeletonType="trainList" />;
    case "success":
      return filteredAndSortedTrains.length > 0 ? (
        <Box data-testid="vehicle-list">
          {groupedTrainData.map((trainDataGroup: TrainData[]) => (
            <TrainListSection
              key={trainDataGroup
                .map(
                  ({ countryCode, trainNumber, trainDate }) =>
                    `${countryCode}/${trainNumber}/${trainDate}`,
                )
                .join("-")}
              trainDatas={trainDataGroup}
              internalMessages={internalMessages}
            />
          ))}
        </Box>
      ) : (
        searchQuery.length < 2 && (
          <NoDataMessage
            style={{ marginTop: "15px" }}
            reason={t("Fant ingen operative tog for øyeblikket")}
          />
        )
      );
    case "error":
      return <FailureMessage style={{ margin: "12px" }} />;
  }
};

export default withErrorBoundary(TrainList);
